// src/components/chat/feedback/FeedbackButtons.tsx

import * as React from "react";
import { FeedbackButtonsProps, FeedbackRating } from "../../../interfaces/feedbackTypes";
import ThumbUpIcon from "../../../../assets/ThumbUp.svg";
import ThumbDownIcon from "../../../../assets/ThumbDown.svg";
import FeedbackModal from "./FeedbackModal";
import { submitFeedback } from "../../../api/feedbackApi";
import { useSession } from "../../../contexts/SessionContext";
import { useAuth } from "../../../contexts/AuthContext";
import { createLogger } from "../../../utils/logger";
import { track } from "../../../utils/analytics";
import { feedbackButtonsStyles, getIconColor } from "./feedbackStyles";

const log = createLogger("FeedbackButtons");

/**
 * FeedbackButtons component displays thumbs up/down icons for user feedback
 * Shows next to the copy button for assistant messages
 */
const FeedbackButtons: React.FC<FeedbackButtonsProps> = ({
  messageId,
  sessionId,
  docId,
  agentId,
  onFeedbackSubmitted,
}) => {
  // Access session context for messages
  const { currentSession } = useSession();

  // Access auth context for access token
  const { accessToken } = useAuth();

  // Track which rating was selected
  const [selectedRating, setSelectedRating] = React.useState<FeedbackRating | null>(null);
  // Track if feedback has been submitted to prevent duplicates
  const [hasSubmitted, setHasSubmitted] = React.useState(false);
  // Track if submission is in progress to prevent concurrent submissions
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  // Modal state
  const [isModalOpen, setIsModalOpen] = React.useState(false);
  // Hover states for visual feedback
  const [hoveredButton, setHoveredButton] = React.useState<FeedbackRating | null>(null);

  /**
   * Helper function to find user question and AI answer
   */
  const getMessageContext = React.useCallback(() => {
    const messages = currentSession.messages;

    // Find the AI message by ID
    const aiMessageIndex = messages.findIndex((m) => m.id === messageId);
    if (aiMessageIndex === -1) {
      log.warn("Could not find AI message", { messageId });
      return { userQuestion: null, aiAnswer: null };
    }

    const aiMessage = messages[aiMessageIndex];

    // Find the preceding user message using array methods
    const userMessage = messages
      .slice(0, aiMessageIndex)
      .reverse()
      .find((m) => m.role === "user");

    return {
      userQuestion: userMessage?.content || null,
      aiAnswer: aiMessage?.content || null,
    };
  }, [currentSession.messages, messageId]);

  /**
   * Handle thumb button click
   */
  const handleThumbClick = (rating: FeedbackRating) => {
    if (hasSubmitted || isSubmitting) {
      log.info("Feedback already submitted or submission in progress", {
        messageId,
        hasSubmitted,
        isSubmitting,
      });
      return;
    }

    log.info("User clicked feedback button", { messageId, rating });
    setSelectedRating(rating);
    setIsModalOpen(true);
  };

  /**
   * Handle feedback submission from modal
   */
  const handleFeedbackSubmit = async (comment: string) => {
    // Prevent duplicate submissions
    if (isSubmitting || hasSubmitted) {
      log.warn("Submission already in progress or completed", {
        messageId,
        isSubmitting,
        hasSubmitted,
      });
      return;
    }

    if (!selectedRating) {
      log.error("No rating selected when submitting feedback");
      return;
    }

    // Mark as submitting to prevent concurrent submissions
    setIsSubmitting(true);

    try {
      // Get the user question and AI answer for context
      const { userQuestion, aiAnswer } = getMessageContext();

      log.info("Submitting feedback", {
        messageId,
        rating: selectedRating,
        hasComment: !!comment,
        hasContext: !!userQuestion && !!aiAnswer,
        hasAccessToken: !!accessToken,
      });

      await submitFeedback({
        request_id: messageId,
        rating: selectedRating,
        comment: comment || undefined,
        session_id: sessionId,
        doc_id: docId,
        agent_id: agentId,
        accessToken: accessToken || undefined,
        metadata: {
          user_question: userQuestion || "Question not found",
          ai_answer: aiAnswer || "Answer not found",
        },
      });

      // Mark as submitted
      setHasSubmitted(true);
      setIsSubmitting(false);

      track("feedback:submitted", {
        rating: selectedRating === "thumbs_up" ? "positive" : "negative",
        agent_id: agentId,
        has_comment: !!comment,
      });
      // Don't close modal here - let the modal handle showing thank you and auto-closing

      // Notify parent if callback provided
      if (onFeedbackSubmitted) {
        onFeedbackSubmitted(selectedRating);
      }

      log.info("Feedback submitted successfully", { messageId });
    } catch (error) {
      log.error("Failed to submit feedback", {
        messageId,
        error: error instanceof Error ? error.message : String(error),
      });
      // Reset submitting state on error so user can retry
      setIsSubmitting(false);
      // Keep modal open on error so user can retry
    }
  };

  /**
   * Get button styles based on state
   */
  const getButtonStyle = (rating: FeedbackRating): React.CSSProperties => {
    const isHovered = hoveredButton === rating;
    return feedbackButtonsStyles.button(hasSubmitted, isHovered);
  };

  /**
   * Get icon fill color based on state
   */
  const getIconFillColor = (rating: FeedbackRating): string => {
    return getIconColor(rating, selectedRating, hoveredButton, hasSubmitted);
  };

  /**
   * Get tooltip text based on state
   */
  const getTooltipText = (rating: FeedbackRating): string => {
    if (hasSubmitted) {
      return selectedRating === rating
        ? `You rated this ${rating === "thumbs_up" ? "helpful" : "unhelpful"}`
        : "";
    }
    return rating === "thumbs_up" ? "Rate as helpful" : "Rate as unhelpful";
  };

  return (
    <>
      {/* Container for feedback buttons with proper alignment */}
      <div style={feedbackButtonsStyles.container}>
        {/* Thumbs up button */}
        <button
          onClick={() => handleThumbClick("thumbs_up")}
          onMouseEnter={() => setHoveredButton("thumbs_up")}
          onMouseLeave={() => setHoveredButton(null)}
          disabled={hasSubmitted}
          title={getTooltipText("thumbs_up")}
          aria-label={getTooltipText("thumbs_up")}
          style={getButtonStyle("thumbs_up")}
        >
          <ThumbUpIcon fill={getIconFillColor("thumbs_up")} />
        </button>

        {/* Thumbs down button */}
        <button
          onClick={() => handleThumbClick("thumbs_down")}
          onMouseEnter={() => setHoveredButton("thumbs_down")}
          onMouseLeave={() => setHoveredButton(null)}
          disabled={hasSubmitted}
          title={getTooltipText("thumbs_down")}
          aria-label={getTooltipText("thumbs_down")}
          style={getButtonStyle("thumbs_down")}
        >
          <ThumbDownIcon fill={getIconFillColor("thumbs_down")} />
        </button>
      </div>

      {/* Feedback modal */}
      <FeedbackModal
        isOpen={isModalOpen}
        rating={selectedRating}
        messageId={messageId}
        sessionId={sessionId}
        docId={docId}
        agentId={agentId}
        onClose={() => {
          setIsModalOpen(false);
          // Modal has been closed after thank you or by user
        }}
        onSubmit={handleFeedbackSubmit}
      />
    </>
  );
};

export default FeedbackButtons;
